/**
 * Pure helpers for uploads whose name is already taken in the facility or facility group.
 * The API reports conflicts (POST api/documents/check-names); the user picks, per file, to add
 * it as a new version of the existing document, keep both under another name, or skip it.
 * No imports, so `node --test` can load this file directly.
 */

export const NAME_CONFLICT_ACTIONS = Object.freeze({
  VERSION: 'version',
  RENAME: 'rename',
  SKIP: 'skip',
});

/** Value of AddDocumentCommand.OnNameConflict that adds the file as a new version. */
export const API_NEW_VERSION = 'NewVersion';

/** check-names request items for drafts ({ documentNumber, documentTitle, fileName }). */
export function toNameItems(drafts) {
  return (drafts || []).map((draft, index) => ({
    key: String(index),
    documentNumber: draft.documentNumber || null,
    documentTitle: draft.documentTitle || null,
    fileName: draft.fileName || null,
  }));
}

/** The conflicting results only, each joined to its draft index. */
export function conflictsOf(results) {
  return (results || [])
    .filter((r) => r && r.conflict)
    .map((r) => ({ ...r, index: Number(r.key) }));
}

/**
 * New version when there is an existing document, else rename (a clash inside the upload).
 * When the existing document has links on its current version, the user must answer whether to
 * transfer them to the new version: `transferLinks` starts unanswered (null).
 */
export function defaultChoice(conflict) {
  const linkCount = (conflict?.existingDocumentId && conflict?.currentLinkCount) || 0;
  return {
    action: conflict?.existingDocumentId ? NAME_CONFLICT_ACTIONS.VERSION : NAME_CONFLICT_ACTIONS.RENAME,
    documentNumber: conflict?.suggestedDocumentNumber || '',
    documentTitle: conflict?.suggestedDocumentTitle || conflict?.suggestedDocumentNumber || '',
    linkCount,
    transferLinks: linkCount > 0 ? null : false,
  };
}

/** True when a choice adds a new version of a document whose links the user must decide about. */
export function asksAboutLinks(choice) {
  return choice?.action === NAME_CONFLICT_ACTIONS.VERSION && choice.linkCount > 0;
}

function extensionOf(fileName) {
  const name = fileName || '';
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot) : '';
}

/**
 * The file name to store for a renamed upload: the new name with the original extension,
 * unless the new name already ends with it ("plot (2).pdf").
 */
export function renamedFileName(originalFileName, newName) {
  const ext = extensionOf(originalFileName);
  const name = (newName || '').trim();
  if (!name) return originalFileName;
  if (ext && name.toLowerCase().endsWith(ext.toLowerCase())) return name;
  return name + ext;
}

/**
 * Applies the user's choices to the drafts. Returns new draft objects, in order, without the
 * skipped ones. Drafts without a choice are returned unchanged.
 *
 * @param {Array} drafts
 * @param {Object<number, {action, documentNumber, documentTitle}>} choices by draft index
 * @param {Array} conflicts from conflictsOf(), for the target document of a new version
 */
export function applyNameChoices(drafts, choices, conflicts) {
  const byIndex = new Map((conflicts || []).map((c) => [c.index, c]));
  const out = [];
  (drafts || []).forEach((draft, index) => {
    const choice = choices?.[index];
    if (!choice) {
      out.push(draft);
      return;
    }
    if (choice.action === NAME_CONFLICT_ACTIONS.SKIP) return;
    if (choice.action === NAME_CONFLICT_ACTIONS.VERSION) {
      out.push({
        ...draft,
        onNameConflict: API_NEW_VERSION,
        targetDocumentId: byIndex.get(index)?.existingDocumentId || null,
        transferLinks: choice.transferLinks === true,
        // The user saw the file is identical to the current version and chose to add it anyway.
        allowDuplicateContent: choice.identical === true,
      });
      return;
    }
    const number = (choice.documentNumber || '').trim();
    const title = (choice.documentTitle || '').trim() || number;
    out.push({
      ...draft,
      documentNumber: number,
      documentTitle: title,
      fileName: renamedFileName(draft.fileName, number),
      onNameConflict: undefined,
      targetDocumentId: undefined,
      transferLinks: undefined,
      allowDuplicateContent: undefined,
    });
  });
  return out;
}

/**
 * A choice the dialog can submit: a rename needs a name, and a new version of a document with
 * links needs an answer to "transfer the links?".
 */
export function isChoiceValid(choice) {
  if (!choice) return false;
  if (choice.action === NAME_CONFLICT_ACTIONS.RENAME) return !!(choice.documentNumber || '').trim();
  if (asksAboutLinks(choice)) return typeof choice.transferLinks === 'boolean';
  return true;
}

/** Label of the "transfer the links" option of a new version. */
export function transferLinksLabel(conflict) {
  const n = conflict?.currentLinkCount || 0;
  const from = conflict?.currentVersionNumber ? ` on v${conflict.currentVersionNumber}` : '';
  return `Transfer the ${n} link${n === 1 ? '' : 's'}${from} to the new version`;
}

/** One line explaining a conflict. */
export function describeConflict(conflict, scopeLabel = 'this facility') {
  if (!conflict) return '';
  if (conflict.existingDocumentId) {
    const name = conflict.existingDocumentNumber || conflict.existingDocumentTitle || 'A document';
    const version = conflict.currentVersionNumber ? ` (current v${conflict.currentVersionNumber})` : '';
    return `"${name}"${version} already exists in ${scopeLabel}.`;
  }
  return 'Another file in this upload has the same name.';
}

/**
 * Label of the "new version" option. `identical`: the file has the current version's bytes, so
 * choosing it means adding a copy on purpose.
 */
export function newVersionLabel(conflict, identical = false) {
  const n = conflict?.currentVersionNumber;
  const base = identical ? 'Upload as a new version anyway' : 'Upload as a new version';
  if (!n) return base;
  if (conflict.isCurrentOverridden) return `${base} (v${n} stays current: it is pinned)`;
  return `${base} (v${n + 1})`;
}

/** True for a draft the user chose to add as a new version of an existing document. */
export function isNewVersionDraft(draft) {
  return draft?.onNameConflict === API_NEW_VERSION;
}

/**
 * Ids of documents whose "new version" upload matched the current version's bytes and so added
 * nothing, from the X-Document-Version-Duplicates header of POST AddDocuments.
 */
export function duplicateIdsFrom(headers) {
  let value;
  if (headers) {
    if (typeof headers.get === 'function') value = headers.get('X-Document-Version-Duplicates');
    value = value ?? headers['X-Document-Version-Duplicates'] ?? headers['x-document-version-duplicates'];
  }
  return new Set(
    `${value || ''}`
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean),
  );
}

/**
 * What happened to each file uploaded as a new version, from the drafts sent and the documents
 * returned (same order).
 * @returns {Array<{ name, documentId, versionNumber, outcome: 'added' | 'pinned' | 'duplicate' }>}
 */
export function versionUploadOutcomes(drafts, documents, duplicateIds) {
  const out = [];
  (drafts || []).forEach((draft, index) => {
    if (!isNewVersionDraft(draft)) return;
    const document = documents?.[index];
    if (!document) return;
    const outcome = duplicateIds?.has(document.id)
      ? 'duplicate'
      : document.isCurrentOverridden
        ? 'pinned'
        : 'added';
    out.push({
      name: document.documentNumber || document.documentTitle || draft.fileName,
      documentId: document.id,
      versionNumber: document.currentVersionNumber ?? null,
      outcome,
    });
  });
  return out;
}

/** Toasts for versionUploadOutcomes(): one per outcome kind, naming the file when there is one. */
export function versionOutcomeMessages(outcomes) {
  const groups = { added: [], pinned: [], duplicate: [] };
  (outcomes || []).forEach((o) => groups[o.outcome]?.push(o));
  const messages = [];
  const v = (n) => (n != null ? `v${n}` : 'the current version');

  if (groups.added.length === 1) {
    const o = groups.added[0];
    messages.push({ type: 'success', text: `"${o.name}" is now ${v(o.versionNumber)}.` });
  } else if (groups.added.length > 1) {
    messages.push({ type: 'success', text: `${groups.added.length} files were added as new versions.` });
  }

  if (groups.pinned.length === 1) {
    const o = groups.pinned[0];
    messages.push({
      type: 'warning',
      text: `"${o.name}" was added as a new version, but ${v(o.versionNumber)} stays current because it is pinned. Use Versions to change it.`,
    });
  } else if (groups.pinned.length > 1) {
    messages.push({
      type: 'warning',
      text: `${groups.pinned.length} files were added as new versions, but their documents keep a pinned current version.`,
    });
  }

  if (groups.duplicate.length === 1) {
    const o = groups.duplicate[0];
    messages.push({
      type: 'info',
      text: `"${o.name}" is identical to the current version (${v(o.versionNumber)}). No new version was created.`,
    });
  } else if (groups.duplicate.length > 1) {
    messages.push({
      type: 'info',
      text: `${groups.duplicate.length} files are identical to their documents' current versions. No new versions were created.`,
    });
  }
  return messages;
}
